/**
 * React hook for authentication state management.
 *
 * This hook provides:
 * - Current user state
 * - Authentication status
 * - Login/logout functions
 * - Loading states
 */

import { useState, useEffect, useCallback } from 'react';
import {
  isAuthenticated,
  getUserData,
  fetchCurrentUser,
  logout as performLogout,
  initiateLogin,
  User,
} from '../lib/auth';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  // Load user data on mount
  useEffect(() => {
    const loadUser = async () => {
      setLoading(true);

      try {
        if (isAuthenticated()) {
          // Try to get user from localStorage first
          const cachedUser = getUserData();

          if (cachedUser) {
            setUser(cachedUser);
            setAuthenticated(true);
          }

          // Fetch fresh user data from API
          try {
            const freshUser = await fetchCurrentUser();
            setUser(freshUser);
            setAuthenticated(true);
          } catch (error) {
            console.error('Failed to fetch user:', error);
            // If fetch fails but we have cached data, keep using it
            if (!cachedUser) {
              setAuthenticated(false);
            }
          }
        } else {
          setAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth check error:', error);
        setAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  // Login function
  const login = useCallback(async () => {
    await initiateLogin();
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    await performLogout();
    setUser(null);
    setAuthenticated(false);
  }, []);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    try {
      const freshUser = await fetchCurrentUser();
      setUser(freshUser);
      setAuthenticated(true);
      return freshUser;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      throw error;
    }
  }, []);

  return {
    user,
    authenticated,
    loading,
    login,
    logout,
    refreshUser,
  };
}
